import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MobileFloatingTabBar from './MobileFloatingTabBar';
import MobileVaultCarousel from './MobileVaultCarousel';
import MobileShopTab from './MobileShopTab';
import MobileClaimTab from './MobileClaimTab';
import MobileProfileTab from './MobileProfileTab';
import MobileCoinCardView from '../MobileCoinCardView';
import MobileOperatorDashboard from './MobileOperatorDashboard';
import { useTheme } from '../../context/ThemeContext';
import {
  parseMobileStyles,
  resolveElementStyle,
  buildBackgroundStyle,
  buildBannerStyle
} from '../../utils/mobileStyleUtils';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.port === '3000' ? `http://${window.location.hostname}:8000` : '');

export default function MobileAppLayout({
  user,
  tenant,
  onUserUpdated,
  onSwitchToDesktop,
  onLogout
}) {
  const isOperator = Boolean(
    user && ['OPERATOR', 'TENANT_ADMIN', 'GLOBAL_ADMIN'].includes(user?.role)
  );

  const [activeTab, setActiveTab] = useState(() => (isOperator ? 'home' : 'vault'));
  const [hasInitializedTab, setHasInitializedTab] = useState(false);
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalVaultCount, setTotalVaultCount] = useState(0);
  const [selectedShopCard, setSelectedShopCard] = useState(null);
  const [activeFullCardCode, setActiveFullCardCode] = useState(null);
  const [activeCardInitialStats, setActiveCardInitialStats] = useState(false);
  const [lastViewedCardCode, setLastViewedCardCode] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  const primaryColor = tenant?.branding_colors?.primary || '#d97706';
  const BATCH_SIZE = 12;

  // Auto-switch to 'home' when user loads if operator
  useEffect(() => {
    if (!hasInitializedTab && user) {
      if (['OPERATOR', 'TENANT_ADMIN', 'GLOBAL_ADMIN'].includes(user?.role)) {
        setActiveTab('home');
      }
      setHasInitializedTab(true);
    }
  }, [user, hasInitializedTab]);

  // Prevent operator from getting stuck on shop or claim tab
  useEffect(() => {
    if (isOperator && (activeTab === 'shop' || activeTab === 'claim')) {
      setActiveTab('home');
    }
  }, [isOperator, activeTab]);

  // Fetch cards belonging to this user's vault
  const fetchCards = useCallback(async () => {
    setLoadingCards(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const params = new URLSearchParams();
      if (isOperator) {
        params.append('status', 'allocated');
        params.append('sort_by', 'newest');
        params.append('limit', String(BATCH_SIZE));
        params.append('offset', '0');
      } else {
        params.append('sort_by', 'newest');
        params.append('limit', '200');
      }
      params.append('summary', 'true');

      const res = await fetch(`${API_BASE}/api/v1/coin-vault/vault?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) throw new Error('Failed to load vault');
      const data = await res.json();
      const fetchedCoins = data?.coins || [];
      setCards(fetchedCoins);
      const total = isOperator
        ? (data?.total_filtered ?? data?.allocated_coins ?? fetchedCoins.length)
        : (data?.total_filtered ?? fetchedCoins.length);
      setTotalVaultCount(total);
      setHasMore(isOperator && fetchedCoins.length < total);

      if (fetchedCoins.length > 0 && !selectedShopCard) {
        setSelectedShopCard(fetchedCoins[0]);
      }
    } catch (err) {
      console.error('Failed to fetch user vault cards:', err);
    } finally {
      setLoadingCards(false);
    }
  }, [isOperator, selectedShopCard]);

  // Progressive load more cards for operators
  const loadMoreCards = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const offset = cards.length;
      const params = new URLSearchParams({
        status: isOperator ? 'allocated' : 'all',
        sort_by: 'newest',
        limit: String(BATCH_SIZE),
        offset: String(offset),
        summary: 'true'
      });
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/vault?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) throw new Error('Failed to load more cards');
      const data = await res.json();
      const newCoins = data?.coins || [];
      if (newCoins.length > 0) {
        setCards(prev => {
          const existingIds = new Set(prev.map(c => c.id || c.public_code));
          const uniqueNew = newCoins.filter(c => !existingIds.has(c.id || c.public_code));
          const combined = [...prev, ...uniqueNew];
          const total = data?.total_filtered ?? data?.allocated_coins ?? combined.length;
          setHasMore(combined.length < total && newCoins.length > 0);
          return combined;
        });
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load more cards:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [cards.length, hasMore, isOperator, loadingMore]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  // Handle opening full scanned view of a card
  const handleOpenFullCard = (card, options = {}) => {
    if (card?.public_code) {
      setActiveFullCardCode(card.public_code);
      setLastViewedCardCode(card.public_code);
      setActiveCardInitialStats(Boolean(options?.openStats));
    }
  };

  // Handle closing full view back to vault carousel
  const handleCloseFullCard = () => {
    setActiveFullCardCode(null);
    setActiveCardInitialStats(false);
  };

  const userDisplayName = user?.nickname || user?.first_name || (user?.email ? user.email.split('@')[0] : 'Collector');

  const { refreshTheme } = useTheme();
  const [refreshing, setRefreshing] = useState(false);

  const handleHeaderRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchCards();
      if (refreshTheme) {
        await refreshTheme(true);
      }
    } catch (err) {
      console.warn('Failed to refresh data and styles:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const mobileStyles = useMemo(() => parseMobileStyles(tenant?.mobile_styles), [tenant?.mobile_styles]);
  const pageKey = isOperator ? `operator:${activeTab}` : `end_user:${activeTab}`;
  const bannerConfig = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'banner').style, [mobileStyles, pageKey]);
  const bgConfig = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'background').style, [mobileStyles, pageKey]);
  const typoConfig = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'typography').style, [mobileStyles, pageKey]);

  const customBgStyle = useMemo(() => buildBackgroundStyle(bgConfig), [bgConfig]);
  const customBannerStyle = useMemo(() => buildBannerStyle(bannerConfig), [bannerConfig]);

  // Keep mobile browser elastic overscroll background in sync with custom theme background
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const prevBg = document.body.style.backgroundColor;
      document.body.style.backgroundColor = customBgStyle.backgroundColor || '#0a0e17';
      return () => {
        document.body.style.backgroundColor = prevBg;
      };
    }
  }, [customBgStyle.backgroundColor]);

  return (
    <div style={{
      minHeight: '100dvh',
      backgroundColor: '#0a0e17',
      color: typoConfig.body_color || '#f8fafc',
      fontFamily: typoConfig.font_family || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      ...customBgStyle
    }}>
      {/* Top Mobile App Navigation Bar */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        backgroundColor: 'rgba(10, 14, 23, 0.88)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        ...customBannerStyle
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {bannerConfig.custom_logo_url || tenant?.logo_url ? (
            <img 
              src={bannerConfig.custom_logo_url || tenant.logo_url} 
              alt={tenant?.brand_name || 'Logo'} 
              style={{
                maxHeight: `${bannerConfig.logo_height || 34}px`,
                maxWidth: '130px',
                objectFit: 'contain',
                display: 'block',
                transform: `translate(${bannerConfig.logo_offset_x || 0}px, ${bannerConfig.logo_offset_y || 0}px)`
              }}
            />
          ) : (
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: primaryColor,
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                <path d="M4 22h16" />
                <path d="M10 14.66V17c0 .55-.45 1-1 1H4v2h16v-2h-5c-.55 0-1-.45-1-1v-2.34" />
                <path d="M12 2a6 6 0 0 1 6 6v5a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8a6 6 0 0 1 6-6z" />
              </svg>
            </div>
          )}

          <div>
            <h1 style={{
              fontSize: bannerConfig.title_font_size ? `${bannerConfig.title_font_size}px` : '14px',
              fontWeight: 800,
              color: bannerConfig.title_color || typoConfig.heading_color || '#ffffff',
              margin: 0,
              lineHeight: 1.1,
              letterSpacing: bannerConfig.title_letter_spacing !== undefined ? `${bannerConfig.title_letter_spacing}px` : '-0.2px',
              fontFamily: typoConfig.font_family
            }}>
              {tenant?.brand_name || 'TrophyCoin'}
            </h1>
            <p style={{
              fontSize: '11px',
              color: bannerConfig.subtitle_color || typoConfig.body_color || '#94a3b8',
              margin: '2px 0 0 0',
              lineHeight: 1,
              fontFamily: typoConfig.font_family
            }}>
              Hi, <span style={{ color: bannerConfig.highlight_color || typoConfig.accent_color || '#fbbf24', fontWeight: 700 }}>{userDisplayName}</span>
            </p>
          </div>
        </div>

        {/* Right header actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {(activeTab === 'vault' || activeTab === 'home') && (
            <button
              onClick={handleHeaderRefresh}
              disabled={loadingCards || refreshing}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: `1px solid ${bannerConfig.border_color || 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '10px',
                padding: '7px 9px',
                color: bannerConfig.subtitle_color || typoConfig.body_color || '#cbd5e1',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Refresh Vault & Styles"
            >
              <svg 
                width="15" 
                height="15" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2.2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{
                  animation: (loadingCards || refreshing) ? 'spin 1s linear infinite' : 'none'
                }}
              >
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
            </button>
          )}

          {!isOperator && (
            <button
              onClick={() => setActiveTab('shop')}
              style={{
                position: 'relative',
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '10px',
                padding: '7px 9px',
                color: '#cbd5e1',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Open Keepsake Shop"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {cartCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: '#f59e0b',
                  color: '#090d16',
                  fontSize: '9.5px',
                  fontWeight: 900,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)'
                }}>
                  {cartCount}
                </span>
              )}
            </button>
          )}
        </div>
      </header>

      {/* Main View Area */}
      <main style={{
        flex: 1,
        width: '100%',
        maxWidth: '520px',
        margin: '0 auto',
        boxSizing: 'border-box'
      }}>
        {activeTab === 'home' && isOperator && (
          <MobileOperatorDashboard
            user={user}
            tenant={tenant}
            mobileStyles={mobileStyles}
            onSelectCard={(coin, opts) => handleOpenFullCard(coin, opts)}
            onViewAllAllocated={() => setActiveTab('vault')}
          />
        )}

        {activeTab === 'vault' && (
          <MobileVaultCarousel
            cards={cards}
            loading={loadingCards}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMoreCards}
            totalCount={totalVaultCount}
            isOperator={isOperator}
            onRefresh={fetchCards}
            onSelectCard={(coin) => handleOpenFullCard(coin)}
            lastViewedCardCode={lastViewedCardCode}
            mobileStyles={mobileStyles}
            onNavigateToShop={(card) => {
              setSelectedShopCard(card);
              setActiveTab('shop');
            }}
          />
        )}

        {activeTab === 'shop' && !isOperator && (
          <MobileShopTab
            user={user}
            tenant={tenant}
            cards={cards}
            initialSelectedCard={selectedShopCard}
            cartCount={cartCount}
            setCartCount={setCartCount}
            mobileStyles={mobileStyles}
          />
        )}

        {activeTab === 'claim' && !isOperator && (
          <MobileClaimTab
            user={user}
            mobileStyles={mobileStyles}
            onCardClaimed={(claimedCard) => {
              fetchCards();
              setActiveTab('vault');
            }}
          />
        )}

        {activeTab === 'profile' && (
          <MobileProfileTab
            user={user}
            tenant={tenant}
            mobileStyles={mobileStyles}
            onUserUpdated={onUserUpdated}
            onSwitchToDesktop={onSwitchToDesktop}
            onLogout={onLogout}
          />
        )}
      </main>

      {/* Floating WhatsApp-Style Bottom Tab Bar */}
      <MobileFloatingTabBar
        activeTab={activeTab}
        onSelectTab={(tab) => {
          setActiveFullCardCode(null);
          setActiveCardInitialStats(false);
          setActiveTab(tab);
        }}
        cartBadgeCount={cartCount}
        user={user}
        isOperator={isOperator}
        mobileStyles={mobileStyles}
      />

      {/* Full-screen card viewer overlay */}
      {activeFullCardCode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
          backgroundColor: '#0a0e17',
          overflowY: 'auto'
        }}>
          <MobileCoinCardView
            publicCode={activeFullCardCode}
            onBack={handleCloseFullCard}
            user={user}
            initialShowStats={activeCardInitialStats}
          />
        </div>
      )}
    </div>
  );
}
